// ═══════════════════════════════════════════════════════════════════════════
//  REMOTE TAMU — /r  atau  /r/KODE
//
//  Dipegang tamu di dalam room. Tanpa login: masuk dengan kode 6 huruf yang
//  tampil di layar TV.
//
//  ── DIRANCANG UNTUK RUANGAN GELAP DAN TANGAN YANG TIDAK STABIL ────────────
//
//  Tombolnya besar, kontrasnya tinggi, dan tidak ada satu pun tindakan yang
//  tidak bisa dibatalkan. Yang paling mudah ditekan tanpa sengaja — batal
//  antre — hanya berlaku untuk lagu yang BELUM diputar.
//
//  ── 5 OKT 2026: TANPA KEPALA, BILAH MENGAMBANG DI BAWAH ──────────────────
//
//  Zaldy: header atas (logo, "Hi · room", jam, kode, input nama, bahasa,
//  "Room tablet") "bikin jelek". Isinya dipindah ke bilah mengambang di bawah
//  yang HANYA berisi: nama room (+outlet), pemilih bahasa, dan kontrol audio
//  (Vokal, Musik, Nada ▲▼ + reset, Jeda/Lanjut, Volume, Lewati). Tertutup:
//  lagu yang diputar + jeda + nada. Dibuka: lembar kontrol. Kode tamu tetap
//  tampil di TV, jadi tidak perlu ada di sini. Nama penyanyi untuk skor jadi
//  isian opsional di lembar kontrol (kosong = "Tamu", skor tetap jalan).
//
//  ── 5 OKT 2026 (sore): BILAH ALA PEMUTAR SPOTIFY, TANPA LEMBAR ───────────
//
//  Zaldy: semua kontrol langsung di bilah — tanpa klik ^ / popup. Bilah kini
//  memuat info lagu, Jeda/Lanjut, Lewati, progress bar (posisi dilaporkan TV,
//  diinterpolasi di sini), Nada ▼▲ + reset, Vokal, Musik, Volume. Lembar
//  kontrol dihapus; nama room, nama penyanyi, dan bahasa pindah ke satu baris
//  tipis di atas halaman (lihat KepalaRoom).
// ═══════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import type { ReactNode, PointerEvent as PointerEventReact } from 'react'
import {
  Trash2, Music2, Mic2, MicOff, Trophy, Loader2, ChevronUp, ChevronDown, Gauge, AlertTriangle,
  Play, Pause, SkipForward, X, Info, User,
  BellRing, MessageCircle, UtensilsCrossed, Volume1, Volume2, VolumeX, RotateCcw, Check, Compass,
  Repeat, GripVertical,
} from 'lucide-react'
import {
  ambilRoom, antre, setelan, batalAntre, tidakBerubah, tandai, urutkanAntrean,
  cekPerangkat, KUNCI_PERANGKAT, jam,
  bacaNama, simpanNama, nadaTampil, pesanGalat, ambilLayanan, panggilWaitress, panggilMarketing,
  antreLuar, antreKaraoke, kodeGalat,
  type Layanan, type LaguLuar, type KeadaanRoom, type BarisAntrean, type PosisiPutar,
} from '../lib/tamu'
import { useBahasa, PilihBahasa, type Kunci } from '../lib/bahasa'
import { tutupKeyboard } from '../lib/keyboard'
// Logo di-IMPOR, bukan ditulis sebagai "/logo-el.png".
//
// Berkas di public/ disalin Vite apa adanya beserta mode-nya; yang bermode 600
// tidak terbaca nginx dan tampil sebagai ikon rusak — ADA tapi tidak terbaca,
// jadi memeriksa "tersalin atau tidak" tidak menemukan apa pun.
//
// Yang di-impor dikeluarkan Vite sendiri ke /assets/ dengan mode 644 dan nama
// ber-hash. Jalur yang sama dengan JS aplikasinya — yang jelas terbaca, karena
// kalau tidak, halaman ini tidak akan muncul sama sekali.
import logoEl from '../assets/logo-el.png'
import PasangLogin from './PasangLogin'
import { JelajahRemote, type AksiJelajah } from './RemoteJelajah'
import { Sampul, Ekualiser, WARNA_KARTU, setKodeRoom, pasangModeRingan } from './RemoteUi'

export default function Remote() {
  // ── TABLET KAMAR: /r/p/<token> memasangkan tablet ini ke satu room ──────
  //
  // Zaldy 29 Sep: "tab terdaftar yang login di kamar-kamar itu otomatis,
  // nggak usah input kode ribet". Tautan pasangan dibuka SEKALI; token-nya
  // disimpan di tablet, alamatnya dibersihkan jadi /r, dan sejak itu remote
  // terbuka sendiri tiap room-nya punya sesi. HP tamu tetap lewat kode.
  const bagian = location.pathname.split('/')
  if (bagian[2] === 'p' && bagian[3]) {
    try { localStorage.setItem(KUNCI_PERANGKAT, bagian[3]) } catch { /* privat */ }
    history.replaceState(null, '', '/r')
  }
  let tokenPerangkat = ''
  try { tokenPerangkat = localStorage.getItem(KUNCI_PERANGKAT) ?? '' } catch { /* privat */ }
  if (tokenPerangkat) return <ModePerangkat token={tokenPerangkat} />
  // 2 Okt: tablet kamar dipasang lewat login staf + pilih room — tanpa kode.
  if (bagian[2] === 'pasang') {
    return <PasangLogin jenis="tablet" kunci={KUNCI_PERANGKAT}
      sesudah={() => location.replace('/r')} batal={() => location.replace('/r')} />
  }

  const awal = (bagian[2] || '').toUpperCase()
  return <RemoteKode awal={awal} />
}

function RemoteKode({ awal }: { awal: string }) {
  const [kode, setKode] = useState(awal)
  const [masuk, setMasuk] = useState(!!awal)

  if (!masuk) return <Gerbang kode={kode} setKode={setKode} lanjut={() => setMasuk(true)} />
  return <Isi kode={kode} keluar={() => { setMasuk(false); history.replaceState(null, '', '/r') }} />
}

// ── MODE PERANGKAT: menunggu sesi, lalu masuk sendiri ───────────────────────
function ModePerangkat({ token }: { token: string }) {
  const [jawab, setJawab] = useState<{ room: string; outlet: string; kode: string; status: string } | null>(null)
  const [galat, setGalat] = useState('')
  const { t: tr } = useBahasa()

  useEffect(() => {
    let hidup = true
    let t: number | undefined
    const tanya = async () => {
      try {
        const j = await cekPerangkat(token)
        if (!hidup) return
        setJawab(j); setGalat('')
      } catch (e) {
        if (!hidup) return
        setGalat(pesanGalat(e))
      }
      // Belum ada sesi: tanya tiap 5 detik. Sudah masuk sesi: tiap 20 detik,
      // cukup untuk menangkap sesi yang ditutup kapten lalu dibuka lagi.
      t = window.setTimeout(tanya, jawab?.kode ? 20000 : 5000)
    }
    tanya()
    return () => { hidup = false; window.clearTimeout(t) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  function lepas() {
    if (!confirm(tr('perangkat.lepasTanya'))) return
    try { localStorage.removeItem(KUNCI_PERANGKAT) } catch { /* privat */ }
    location.replace('/r')
  }

  if (jawab?.kode) {
    return <Isi key={jawab.kode} kode={jawab.kode} keluar={lepas} perangkat />
  }

  return (
    <div className="relative min-h-screen grid place-items-center px-6 text-center" style={{ background: LATAR }}>
      <div className="absolute right-4 top-[calc(env(safe-area-inset-top)+12px)] z-10"><PilihBahasa /></div>
      <div className="max-w-md rm-muncul">
        <img src={logoEl} alt="El" className="w-16 h-16 mx-auto rounded-2xl object-contain bg-gold/10 border border-gold/25 p-1"
          onError={(e) => { e.currentTarget.style.visibility = 'hidden' }} />
        <div className="mt-5 text-[12px] tracking-[0.25em] uppercase text-gold-1">{jawab ? `${jawab.outlet} · ${jawab.room}` : tr('perangkat.tablet')}</div>
        <h1 className="mt-2 text-[26px] font-semibold">
          {galat ? tr('perangkat.belumTerhubung') : tr('perangkat.menunggu')}
        </h1>
        <p className="mt-2 text-[14px] text-text-3 leading-relaxed">
          {galat
            ? galat
            : tr('perangkat.petunjuk')}
        </p>
        {!galat && <Loader2 size={20} className="animate-spin text-text-3 mx-auto mt-6" />}
        <button onClick={lepas} className="mt-10 text-[12px] text-text-3 underline underline-offset-4">
          {tr('perangkat.lepas')}
        </button>
      </div>
    </div>
  )
}

// ── GERBANG KODE ───────────────────────────────────────────────────────────

function Gerbang({ kode, setKode, lanjut }: {
  kode: string; setKode: (v: string) => void; lanjut: () => void
}) {
  const [sibuk, setSibuk] = useState(false)
  const [galat, setGalat] = useState('')
  const { t } = useBahasa()

  async function coba() {
    const k = kode.trim().toUpperCase()
    if (k.length < 4) { setGalat(t('gerbang.pendek')); return }
    setSibuk(true); setGalat('')
    try {
      await ambilRoom(k)
      // Diuji DULU, baru masuk. Membiarkan masuk lalu gagal di dalam berarti
      // pesan galatnya muncul di layar yang tidak punya kolom kode — dan tamu
      // tidak punya jalan memperbaikinya selain memuat ulang.
      history.replaceState(null, '', `/r/${k}`)
      setKode(k); lanjut()
    } catch (e) { setGalat(pesanGalat(e)) } finally { setSibuk(false) }
  }

  return (
    <div className="relative min-h-screen grid place-items-center px-6" style={{ background: LATAR }}>
      <div className="absolute right-4 top-[calc(env(safe-area-inset-top)+12px)] z-10"><PilihBahasa /></div>
      <div className="w-full max-w-sm text-center rm-muncul">
        <img src={logoEl} alt="El" width={56} height={56}
          className="w-14 h-14 mx-auto rounded-2xl object-contain bg-gold/10 border border-gold/25 p-1"
          onError={(e) => { e.currentTarget.style.visibility = 'hidden' }} />
        <h1 className="mt-4 text-[22px] font-semibold">{t('gerbang.judul')}</h1>
        <p className="mt-1 text-[14px] text-text-3">
          {t('gerbang.petunjuk')}
        </p>

        <input
          value={kode}
          onChange={e => setKode(e.target.value.toUpperCase().slice(0, 8))}
          onKeyDown={e => e.key === 'Enter' && coba()}
          placeholder="ABC123"
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          className="mt-6 w-full text-center tracking-[0.4em] text-[26px] font-semibold
                     !bg-white/[0.05] !border-white/10 !rounded-2xl !py-4 outline-none
                     focus:!border-gold/50"
        />
        {galat && (
          <div className="mt-3 text-[13.5px] text-red-400 flex items-start gap-1.5 text-left">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" /> <span>{galat}</span>
          </div>
        )}
        <button onClick={coba} disabled={sibuk}
          className="rm-tekan mt-4 w-full py-3.5 rounded-2xl bg-gradient-to-b from-gold-1 to-gold text-black font-semibold
                     disabled:opacity-50 flex items-center justify-center gap-2">
          {sibuk ? <Loader2 size={17} className="animate-spin" /> : null} {t('gerbang.masuk')}
        </button>
        {/* Staf: pasang tablet kamar sekali lewat login — sesudahnya tablet
            terbuka sendiri tiap booking di room itu dimulai. */}
        <button onClick={() => location.assign('/r/pasang')}
          className="mt-8 text-[13px] text-text-3 underline underline-offset-4">
          Staf: pasang tablet kamar (login)
        </button>
      </div>
    </div>
  )
}

// ── ISI ────────────────────────────────────────────────────────────────────
//
// ═══════════════ TATA LETAK (5 Okt 2026) ═══════════════════════════════════
//
//   ┌──────────────────────────────┬───────────────┐
//   │  (banner promo dibuang 30 Okt)│  ANTREAN      │
//   ├──────────────────────────────┤  ▮▮ diputar   │
//   │ [Spotify|YouTube|ElGroup|Cari]  02 …         │
//   │ (ID)(EN)(ZH)(JA)(KR)      🔍 ├───────────────┤
//   │  daftar lagu — satu ketukan  │  LAYANAN      │
//   │                              │  SKOR         │
//   ├──────────────────────────────┴───────────────┤
//   │ ◖ bilah putar: lagu │ ❚❚ ⏭ + progres │ nada · vokal · musik · vol ◗ │
//   └──────────────────────────────────────────────┘
//   (baris tipis di atas: room · nama penyanyi · bahasa)
//
// Di HP kolom kanan turun ke tab bawah (Jelajah · Antrean · Layanan · Skor);
// bilah putar duduk tepat di atas tab itu. Tinggi bilah yang sebenarnya
// ditulis ke --rm-bilah (lihat BilahMelayang) dan dipakai padding bawah
// halaman (.rm-alas-bawah), letak toast, dan tinggi kolom kanan.

type Tab = 'jelajah' | 'antre' | 'papan' | 'layanan'
type Kabar = { teks: string; jenis: 'ok' | 'putar' | 'galat' | 'info'; n: number }

const LATAR = 'radial-gradient(1100px 600px at -5% -10%, rgba(78,92,214,.32), transparent 60%), radial-gradient(900px 500px at 105% 0%, rgba(150,80,255,.2), transparent 55%), radial-gradient(800px 500px at 50% 115%, rgba(212,175,55,.12), transparent 60%), linear-gradient(180deg, #0b0e22 0%, #0d1130 45%, #0a0c1c 100%)'

function Isi({ kode, keluar, perangkat = false }: { kode: string; keluar: () => void; perangkat?: boolean }) {
  setKodeRoom(kode)
  // 27 Okt: tablet lemah (Huawei dsb.) → html.ringan, lihat RemoteUi.
  useEffect(() => { pasangModeRingan() }, [])
  const [data, setData] = useState<KeadaanRoom | null>(null)
  const [tab, setTab] = useState<Tab>('jelajah')
  const [galat, setGalat] = useState('')
  const [nama, setNama] = useState(bacaNama())
  const [kabar, setKabar] = useState<Kabar | null>(null)
  const { t: tr } = useBahasa()
  // Posisi lagu dari TV: disimpan di luar state React supaya poll 4 dtk
  // hanya menggambar ulang progress bar, bukan seluruh halaman.
  const papanPosisi = useMemo(() => buatPapanPosisi(), [])

  const sidikRef = useRef('')
  const jalanRef = useRef<Promise<void> | null>(null)

  // `paksa` = minta jawaban penuh DAN wajib jalan; penjaga tumpang-tindih
  // menolak poll saat masih ada yang menggantung, tapi permintaan dari tombol
  // ditunggu, bukan dibuang (lihat riwayat berkas ini untuk alasannya).
  const muat = useCallback(async (paksa = false, signal?: AbortSignal) => {
    if (jalanRef.current) {
      if (!paksa) return
      await jalanRef.current.catch(() => {})
    }
    const kerja = (async () => {
      try {
        const j = await ambilRoom(kode, paksa ? '' : sidikRef.current, signal)
        sidikRef.current = j.sidik ?? ''
        // Posisi ikut di jawaban penuh MAUPUN "tetap" (di luar sidik).
        papanPosisi.tulis(j.posisi ? { ...j.posisi, diterima: performance.now() } : null)
        if (!tidakBerubah(j)) setData(j)
        setGalat('')
      } catch (e) {
        if ((e as { code?: string })?.code === 'ERR_CANCELED') return
        sidikRef.current = ''
        setGalat(pesanGalat(e))
      }
    })()
    jalanRef.current = kerja
    try { await kerja } finally { jalanRef.current = null }
  }, [kode, papanPosisi])

  const segarkan = useCallback(() => { void muat(true) }, [muat])

  useEffect(() => {
    const ac = new AbortController()
    let t: number | undefined
    const putar = () => {
      t = window.setTimeout(async () => { await muat(false, ac.signal); putar() }, 4000)
    }
    const lihat = () => {
      if (document.hidden) window.clearTimeout(t)
      else { muat(false, ac.signal); window.clearTimeout(t); putar() }
    }
    muat(true, ac.signal)
    putar()
    document.addEventListener('visibilitychange', lihat)
    return () => {
      window.clearTimeout(t)
      document.removeEventListener('visibilitychange', lihat)
      ac.abort()
    }
  }, [muat])

  useEffect(() => {
    if (!kabar) return
    const t = setTimeout(() => setKabar(null), kabar.jenis === 'galat' || kabar.jenis === 'info' ? 4200 : 2800)
    return () => clearTimeout(t)
  }, [kabar])

  const tampil = (teks: string, jenis: Kabar['jenis'] = 'ok') => setKabar({ teks, jenis, n: Date.now() })

  const antrean = data?.antrean ?? []
  const sedang = antrean.find(a => a.status === 'diputar')
  const berikut = antrean.find(a => a.status === 'menunggu')

  // Kalimat sesudah antre: server memberi tahu apakah lagu langsung diputar.
  const kabarAntre = (diputar: boolean | undefined, judul: string) =>
    tampil(diputar ? tr('isi.diputarSekarang', { judul }) : tr('isi.masukAntrean', { judul }), diputar ? 'putar' : 'ok')

  const aksiJelajah: AksiJelajah = {
    lokal: async (songId, judul) => {
      try {
        const j = await antre(kode, songId, nama)
        simpanNama(nama)
        kabarAntre(j.diputar, judul)
        segarkan()
      } catch (e) { tampil(pesanGalat(e), 'galat') }
    },
    youtube: async (l: LaguLuar) => {
      try {
        const j = await antreLuar(kode, {
          sumber: 'youtube', ref: l.id, judul: l.judul, artis: l.artis,
          sampul: l.sampul, durasi: l.durasi, nama,
        })
        simpanNama(nama)
        kabarAntre(j.diputar, l.judul)
        segarkan()
      } catch (e) { tampil(pesanGalat(e), 'galat') }
    },
    karaoke: async (l: LaguLuar) => {
      try {
        const j = await antreKaraoke(kode, { judul: l.judul, artis: l.artis, sampul: l.sampul, nama })
        simpanNama(nama)
        tampil(tr(j.diputar ? 'karaoke.diputar' : 'karaoke.masuk', { judul: l.judul }), j.diputar ? 'putar' : 'ok')
        segarkan()
      } catch (e) {
        const k = kodeGalat(e)
        tampil(k === 'kuota' ? tr('karaoke.kuota')
          : k === 'kunci' ? tr('karaoke.kunci')
          : k === 'tidak_ada' ? tr('karaoke.tidakAda', { judul: l.judul })
          : pesanGalat(e), 'galat')
      }
    },
  }

  // Kontrol lagu yang sedang diputar (bilah mengambang).
  async function ubahSedang(isi: IsiSetelan) {
    if (!sedang) return
    try { await setelan(kode, sedang.id, isi); segarkan() } catch (e) { tampil(pesanGalat(e), 'galat') }
  }
  async function lewati() {
    if (!sedang) return
    try { await tandai(kode, sedang.id, 'lewati'); segarkan() } catch (e) { tampil(pesanGalat(e), 'galat') }
  }

  const papan = data?.papan ?? []
  const room = data?.room ?? null
  // Kenapa belum ada yang berbunyi padahal antrean terisi.
  const statusSesi = room?.status === 'jeda' ? tr('isi.dijeda')
    : room?.status === 'habis' ? tr('isi.habis')
    : room?.status === 'siap' ? tr('bilah.menungguSesi') : ''

  return (
    <div className="rm-alas-bawah rm-layar min-h-screen tab:min-h-0 relative overflow-x-hidden text-text" style={{ background: LATAR }}>
      <KepalaRoom room={room} statusSesi={statusSesi} nama={nama} setNama={setNama} />

      {galat && (
        <div className="mx-4 tab:mx-6 mt-3 text-[13px] text-red-200 border border-red-500/30 bg-red-500/10
                        rounded-2xl px-3.5 py-2.5 flex items-center gap-2 rm-pudar">
          <AlertTriangle size={14} className="shrink-0" />
          <span className="flex-1 min-w-0">{galat}</span>
          {!perangkat && (
            <button onClick={keluar} className="shrink-0 text-[12.5px] font-medium px-3 py-1 rounded-full border border-red-300/30 hover:bg-red-500/10">
              {tr('isi.gantiKode')}
            </button>
          )}
        </div>
      )}

      {/* 6 Okt: di ≥ lg grid ini mengisi sisa tinggi layar (rm-layar). Banner &
          baris sumber diam; yang digulir hanya daftar lagu (JelajahRemote,
          gulirDaftar) dan kolom kanan (rm-gulir) — dua-duanya berhenti di atas
          bilah putar karena halaman diberi padding-bawah setinggi bilah. */}
      <div className="px-4 tab:px-6 pt-3 grid gap-5 tab:grid-cols-[minmax(0,1fr)_360px] xl:grid-cols-[minmax(0,1fr)_400px]
                      tab:flex-1 tab:min-h-0 tab:grid-rows-[minmax(0,1fr)]">
        <main className={`min-w-0 ${tab === 'jelajah' ? '' : 'hidden tab:flex'} tab:flex tab:flex-col tab:min-h-0 tab:overflow-y-auto tab:overscroll-contain rm-tanpa-gulir`}>
          {/* 30 Okt (Zaldy): banner promo/iklan di atas dibuang — ruangnya untuk daftar lagu. */}
          <JelajahRemote kode={kode} aksi={aksiJelajah} gulirDaftar />
        </main>

        {/* 22 Okt (Zaldy): "section-nya tetap, yang ke-scroll cuma list-nya".
            Di ≥ tab kolom kanan TIDAK digulir utuh: Layanan & Papan skor diam,
            kartu Antrean mengecil mengikuti sisa tinggi dan daftar lagunya
            yang digulir (lentur). */}
        <aside className={`min-w-0 ${tab === 'jelajah' ? 'hidden tab:flex' : ''} tab:flex tab:flex-col tab:min-h-0 tab:overflow-hidden`}>
          <div key={tab} className="space-y-4 rm-pudar tab:space-y-0 tab:flex tab:flex-col tab:gap-4 tab:min-h-0 tab:flex-1 tab:pb-2">
            {(tab === 'antre' || tab === 'jelajah') && (
              <div className="tab:min-h-0 tab:flex tab:flex-col">
                <PanelAntrean kode={kode} antrean={antrean} sesudah={segarkan} />
              </div>
            )}
            {(tab === 'layanan' || tab === 'jelajah') && (
              <div className="tab:shrink-0">
                <PanelLayanan kode={kode} kabari={s => tampil(s)} />
              </div>
            )}
            {(tab === 'papan' || (tab === 'jelajah' && papan.length > 0)) && (
              <div className="tab:shrink-0">
                <Kartu judul={tr('papan.judul')} ikon={<Trophy size={15} className="text-gold" />}>
                  <TabPapan papan={tab === 'papan' ? papan : papan.slice(0, 3)} />
                </Kartu>
              </div>
            )}
          </div>
        </aside>
      </div>

      <BilahMelayang sedang={sedang} berikut={berikut} statusSesi={statusSesi}
        ubah={ubahSedang} lewati={lewati} posisi={papanPosisi} kabari={s => tampil(s, 'info')} />

      <nav className="tab:hidden fixed bottom-0 inset-x-0 z-30 bg-[#0c0f24] border-t border-white/[0.07]
                      grid grid-cols-4 pb-[env(safe-area-inset-bottom)]">
        {([
          ['jelajah', tr('tab.jelajah'), <Compass size={19} key="a" />],
          ['antre', tr('tab.antre'), <Music2 size={19} key="b" />],
          ['layanan', tr('tab.layanan'), <BellRing size={19} key="d" />],
          ['papan', tr('tab.skor'), <Trophy size={19} key="c" />],
        ] as const).map(([id, label, ikon]) => (
          <button key={id} onClick={() => setTab(id as Tab)}
            className={`rm-tekan relative py-2.5 flex flex-col items-center gap-1 text-[11.5px] font-medium ${tab === id ? 'text-gold-1' : 'text-text-3'}`}>
            <span className={`absolute top-0 h-[2px] rounded-full bg-gold transition-all duration-300 ${tab === id ? 'w-8 opacity-100' : 'w-0 opacity-0'}`} />
            <span className="relative">
              {ikon}
              {id === 'antre' && antrean.length > 0 && (
                <span className="absolute -top-1.5 -right-2.5 min-w-[16px] h-4 px-1 rounded-full bg-gold text-black text-[10.5px] font-bold grid place-items-center">
                  {antrean.length}
                </span>
              )}
            </span>
            {label}
          </button>
        ))}
      </nav>

      {kabar && (
        <div key={kabar.n}
          className={`rm-toast rm-toast-letak fixed left-1/2 -translate-x-1/2 z-[60]
                      max-w-[92vw] w-max text-[14px] pl-3 pr-4 py-2.5 rounded-full font-medium flex items-center gap-2
                      shadow-[0_14px_40px_-10px_rgba(0,0,0,.8)] border
                      ${kabar.jenis === 'galat' ? 'bg-[#2a1220]/95 text-red-100 border-red-400/30'
                        : kabar.jenis === 'info' ? 'bg-[#1a1f45]/95 text-text border-white/15 backdrop-blur-md !rounded-2xl'
                        : 'bg-gradient-to-r from-gold-1 to-gold text-black border-gold-1/60'}`}>
          <span className={`w-6 h-6 rounded-full grid place-items-center shrink-0 ${
            kabar.jenis === 'galat' ? 'bg-red-400/20' : kabar.jenis === 'info' ? 'bg-white/10 text-gold-1' : 'bg-black/15'}`}>
            {kabar.jenis === 'galat' ? <AlertTriangle size={13} />
              : kabar.jenis === 'info' ? <Info size={13} />
              : kabar.jenis === 'putar' ? <Play size={12} fill="currentColor" /> : <Check size={14} />}
          </span>
          {/* Keterangan (info) boleh dua baris — kalimatnya lebih panjang. */}
          <span className={`min-w-0 ${kabar.jenis === 'info' ? 'line-clamp-2 leading-snug text-[13.5px]' : 'truncate'}`}>{kabar.teks}</span>
        </div>
      )}
    </div>
  )
}

// lentur (22 Okt): di ≥ tab kartu boleh mengecil mengikuti sisa tinggi kolom;
// isinya (daftar) yang menggulir, judul kartu tetap di tempat.
const Kartu = ({ judul, ikon, kanan, children, lentur }: { judul: string; ikon: ReactNode; kanan?: ReactNode; children: ReactNode; lentur?: boolean }) => (
  <section className={`rounded-[24px] bg-white/[0.035] border border-white/[0.08] p-4 shadow-[0_20px_50px_-30px_rgba(0,0,0,.9)]
    ${lentur ? 'tab:flex tab:flex-col tab:min-h-0' : ''}`}>
    <div className="flex items-center gap-2 mb-3 shrink-0">
      {ikon}
      <span className="text-[14.5px] font-semibold tracking-tight">{judul}</span>
      <span className="flex-1" />
      {kanan}
    </div>
    {children}
  </section>
)

// ── BARIS TIPIS DI ATAS: room · nama penyanyi · bahasa ─────────────────────
//
// Pengganti isi lembar kontrol yang dihapus. Satu baris setinggi tombol kecil
// — bukan kepala besar yang dulu dibuang (logo, jam, kode, dsb).
function KepalaRoom({ room, statusSesi, nama, setNama }: {
  room: KeadaanRoom['room']; statusSesi: string; nama: string; setNama: (v: string) => void
}) {
  const { t } = useBahasa()
  return (
    <header className="relative z-30 px-4 tab:px-6 pt-[calc(env(safe-area-inset-top)+10px)] flex items-center gap-2">
      <div className="min-w-0 flex-1 flex items-center gap-2">
        <span className={`w-2 h-2 rounded-full shrink-0 ${statusSesi ? 'bg-amber-400' : 'bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,.75)]'}`} />
        <span className="truncate text-[14.5px] font-semibold tracking-tight">{room?.nama ?? t('isi.room')}</span>
        {room?.outlet && <span className="hidden sm:inline truncate text-[13px] text-text-3">· {room.outlet}</span>}
        {statusSesi && (
          <span className="shrink-0 text-[11px] px-2 py-0.5 rounded-full border border-amber-400/40 text-amber-300 truncate max-w-[40vw]">{statusSesi}</span>
        )}
      </div>
      {/* Nama penyanyi — opsional; kosong = "Tamu" di papan skor. */}
      <label title={t('bilah.nama')}
        className="shrink-0 flex items-center gap-1.5 h-8 pl-2.5 pr-2 rounded-full bg-white/[0.05] border border-white/10 text-text-3
                   focus-within:border-gold/50 w-[118px] sm:w-[180px] transition-colors">
        <User size={13} className="shrink-0" />
        <input value={nama} onChange={e => setNama(e.target.value.slice(0, 40))} onBlur={() => simpanNama(nama)}
          enterKeyHint="done"
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); tutupKeyboard(e.currentTarget) } }}
          placeholder={t('isi.namaKamu')} aria-label={t('bilah.nama')}
          className="min-w-0 flex-1 !bg-transparent !border-0 !p-0 !rounded-none !text-[13px] !text-text placeholder:text-text-3" />
      </label>
      <PilihBahasa />
    </header>
  )
}

// ── BILAH PUTAR (5 Okt 2026 sore — ala pemutar Spotify) ───────────────────
//
// Zaldy: "seperti pemutar Spotify, SEMUA kontrol langsung di bilah — tanpa
// harus klik ^ / popup". Satu struktur, dua tata letak lewat grid-area:
//
//   HP / tablet tegak (< lg):
//     ┌────────────────────────────────────────────┐
//     │ ▣ PAUSED · Judul / artis          ( ❚❚ ) ⏭ │  info · tombol
//     │ 1:23 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ 3:45 │  progres
//     │ ▼ Nada+1 ▲ ↺   [Vokal] [Musik]   [Vol] (i) │  kontrol
//     └────────────────────────────────────────────┘
//   Tablet mendatar / desktop (≥ lg) — satu baris lebar:
//     ▣ Judul / artis │      ( ❚❚ )  ⏭       │ ▼Nada▲↺ [Vokal][Musik] 🔊━━━ (i)
//                     │ 1:23 ━━━━━━━━━━ 3:45 │
//
// Tombol yang tidak didukung sumbernya (vokal/nada YouTube tanpa EL Audio,
// semua kontrol Spotify) TETAP tampil — redup; diketuk memunculkan alasannya
// (title tidak terbaca di layar sentuh). Volume di HP satu-satunya popover.

type PosisiDiterima = PosisiPutar & { diterima: number }
type PapanPosisi = {
  baca: () => PosisiDiterima | null
  tulis: (p: PosisiDiterima | null) => void
  dengar: (f: () => void) => () => void
}

/** Wadah kecil di luar React untuk posisi dari TV (useSyncExternalStore). */
function buatPapanPosisi(): PapanPosisi {
  let nilai: PosisiDiterima | null = null
  const pendengar = new Set<() => void>()
  return {
    baca: () => nilai,
    tulis: p => { nilai = p; pendengar.forEach(f => f()) },
    dengar: f => { pendengar.add(f); return () => { pendengar.delete(f) } },
  }
}

function BilahMelayang({ sedang, berikut, statusSesi, ubah, lewati, posisi, kabari }: {
  sedang?: BarisAntrean; berikut?: BarisAntrean; statusSesi: string
  ubah: (isi: IsiSetelan) => void; lewati: () => void
  posisi: PapanPosisi; kabari: (s: string) => void
}) {
  const { t } = useBahasa()
  const ref = useRef<HTMLDivElement>(null)

  // Tinggi bilah yang sebenarnya (1 baris kosong / 3 baris HP / 1 baris lg)
  // → --rm-bilah. Padding bawah halaman, toast, dan kolom kanan mengikutinya.
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const tulis = () => document.documentElement.style.setProperty('--rm-bilah', `${Math.ceil(el.offsetHeight)}px`)
    tulis()
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(tulis)
    ro.observe(el)
    return () => { ro.disconnect(); document.documentElement.style.removeProperty('--rm-bilah') }
  }, [])

  return (
    <div className="fixed z-40 inset-x-3 bottom-[calc(env(safe-area-inset-bottom)+68px)] tab:inset-x-6 tab:bottom-4">
      <div ref={ref}
        className="mx-auto max-w-[1440px] rounded-[22px] border border-white/[0.12] bg-[#141938]
                   shadow-[0_12px_28px_-12px_rgba(0,0,0,.9)] px-3 py-2.5 tab:px-4 tab:py-3 rm-muncul">
        {sedang ? (
          <KontrolPutar key={sedang.id} a={sedang} ubah={ubah} lewati={lewati} posisi={posisi} kabari={kabari} />
        ) : (
          <div className="flex items-center gap-3 rm-pudar">
            <span className="w-11 h-11 rounded-2xl shrink-0 grid place-items-center bg-gradient-to-br from-gold/25 to-violet-500/20 border border-white/10 text-gold-1">
              <Mic2 size={19} />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-[11px] uppercase tracking-[0.16em] text-gold-1/85 truncate">{t('antre.belumPutar')}</span>
              <span className="block text-[15px] font-semibold truncate leading-snug">
                {berikut && statusSesi ? statusSesi : t('bilah.pilihLagu')}
              </span>
              <span className="block text-[12px] text-text-3 truncate">
                {berikut ? t('antre.berikutnya', { judul: berikut.judul }) : t('bilah.kosongSub')}
              </span>
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

/** Tombol yang bisa "tidak didukung": tetap tampil redup, diketuk = alasan.
 *  `mati` = batas biasa (mis. nada sudah +6) → disabled sungguhan. */
function TombolLunak({ bisa, mati = false, alasan, kabari, onClick, label, className = '', children }: {
  bisa: boolean; mati?: boolean; alasan?: string; kabari: (s: string) => void
  onClick: () => void; label: string; className?: string; children: ReactNode
}) {
  return (
    <button type="button" aria-label={label} title={!bisa && alasan ? alasan : label}
      aria-disabled={!bisa || undefined} disabled={bisa && mati}
      onClick={() => { if (!bisa) { if (alasan) kabari(alasan); return } onClick() }}
      className={`rm-tekan ${className} ${bisa ? '' : 'opacity-35 cursor-not-allowed'} disabled:opacity-30`}>
      {children}
    </button>
  )
}

function KontrolPutar({ a, ubah, lewati, posisi, kabari }: {
  a: BarisAntrean; ubah: (isi: IsiSetelan) => void; lewati: () => void
  posisi: PapanPosisi; kabari: (s: string) => void
}) {
  const { t } = useBahasa()
  const { lokal, sp, yt, lap, vokalBisa, musikBisa, nadaBisa, jedaBisa, volBisa } = kemampuan(a)
  const ytEfek = yt && (vokalBisa || nadaBisa)
  const alasanSp = t('kontrol.spotifyLokal')
  const alasan = sp ? alasanSp : yt && !ytEfek ? t('kontrol.ytPerluElAudio') : ''
  const alasanVokal = alasan || (yt ? t('kontrol.ytPerluElAudio') : t('kontrol.belumDiproses'))
  const alasanNada = alasanVokal
  // Satu kalimat keterangan untuk tombol (i) — dulu baris kecil di lembar.
  const catatan = alasan || (ytEfek ? t('kontrol.ytPengurang') : '') || lap?.pesan || (!vokalBisa ? alasanVokal : '')
  const vokalNyala = lokal || (yt && vokalBisa) ? a.vokal_panduan : true
  const musik = a.musik_nyala ?? true
  const nadaLabel = a.geser_nada > 0 ? `+${a.geser_nada}` : String(a.geser_nada)

  // Ukuran dihitung untuk HP 360 px: baris kontrol muat tanpa gulir.
  // 2 Nov 2026 — Zaldy: nada, vokal, musik, volume DIPERBESAR (jari tamu di room gelap).
  const tombolNada = 'w-10 h-11 rounded-full grid place-items-center hover:bg-white/10'
  const coretan = <span className="absolute left-1/2 top-1/2 w-[26px] h-[2px] rounded bg-current -translate-x-1/2 -translate-y-1/2 rotate-45" />

  return (
    // 2 Nov 2026 — Zaldy: "masih ada image, title dll — section bawah gausah
    // ada, gedein section yang lain aja". Sampul/judul/artis dicabut dari
    // kontrol bawah (judul sudah ada di daftar antrean); tombol putar,
    // progres, dan nada/vokal/musik/volume mengisi lebarnya.
    // 2 Nov sore — Zaldy: "kanan-kiri kaya awal, cuma takeout bar tulisan
    // tadi" → tata letak sebaris seperti semula (tombol · progres · nada…),
    // hanya kolom info yang hilang; bilah tidak menebal ke bawah.
    <div className="grid items-center gap-x-3 gap-y-2
                    grid-cols-[auto_minmax(0,1fr)] [grid-template-areas:'tombol_prog''ext_ext']
                    tab:gap-x-5 tab:grid-cols-[auto_minmax(200px,1fr)_auto] tab:[grid-template-areas:'tombol_prog_ext'] rm-pudar">
      {/* ── Jeda/Lanjut + Lewati — selalu terlihat ── */}
      <div className="[grid-area:tombol] flex items-center gap-1.5 tab:gap-2">
        <TombolLunak bisa={jedaBisa} alasan={alasanSp} kabari={kabari}
          label={a.jeda ? t('kontrol.lanjut') : t('kontrol.jeda')} onClick={() => ubah({ jeda: !a.jeda })}
          className="w-14 h-14 rounded-full grid place-items-center bg-gradient-to-b from-gold-1 to-gold text-black
                     shadow-[0_8px_22px_-8px_rgba(212,175,55,.85)] hover:brightness-110">
          {a.jeda ? <Play size={24} fill="currentColor" className="ml-0.5" /> : <Pause size={24} fill="currentColor" />}
        </TombolLunak>
        <button type="button" onClick={lewati} aria-label={t('antre.lewati')} title={t('antre.lewati')}
          className="rm-tekan w-12 h-12 rounded-full grid place-items-center text-text-2 hover:text-text hover:bg-white/[0.08]">
          <SkipForward size={22} fill="currentColor" />
        </button>
        {/* 8 Okt 2026 — Zaldy: "tombol looping kalau mau diputar terus-terusan".
            Per lagu; TV mengulang dari awal saat habis. Spotify tidak bisa
            (diputar aplikasi lain). */}
        <TombolLunak bisa={!sp} alasan={alasanSp} kabari={kabari}
          label={a.ulang ? t('kontrol.ulangNyala') : t('kontrol.ulang')}
          onClick={() => { ubah({ ulang: !a.ulang }); kabari(a.ulang ? t('kontrol.ulangMati') : t('kontrol.ulangNyala')) }}
          className={`w-12 h-12 rounded-full grid place-items-center hover:bg-white/[0.08] ${
            a.ulang ? 'text-gold-1 bg-gold/15 ring-1 ring-gold/50' : 'text-text-2 hover:text-text'}`}>
          <Repeat size={20} />
        </TombolLunak>
      </div>

      {/* ── Progres ── */}
      <Progres a={a} papan={posisi} perkiraan={sp} />

      {/* ── Nada · Vokal · Musik · Volume ── */}
      <div className="[grid-area:ext] flex flex-wrap tab:flex-nowrap items-center justify-center gap-2 min-w-0 tab:justify-self-end">
        <div className="flex items-center shrink-0 rounded-full bg-white/[0.05] border border-white/10 p-0.5"
          title={nadaBisa ? undefined : alasanNada}>
          <TombolLunak bisa={nadaBisa} mati={a.geser_nada <= -6} alasan={alasanNada} kabari={kabari}
            label={`${t('kontrol.nada')} −`} onClick={() => ubah({ geser_nada: Math.max(-6, a.geser_nada - 1) })}
            className={tombolNada}><ChevronDown size={22} /></TombolLunak>
          <span className={`w-11 text-center leading-none ${nadaBisa ? '' : 'opacity-40'}`}>
            <span className="block text-[11px] font-semibold uppercase tracking-wider text-text-3 truncate">{t('kontrol.nada')}</span>
            <span className={`block mt-1 text-[18px] font-bold tabular-nums ${a.geser_nada !== 0 ? 'text-gold-1' : 'text-text-2'}`}>{nadaLabel}</span>
          </span>
          <TombolLunak bisa={nadaBisa} mati={a.geser_nada >= 6} alasan={alasanNada} kabari={kabari}
            label={`${t('kontrol.nada')} +`} onClick={() => ubah({ geser_nada: Math.min(6, a.geser_nada + 1) })}
            className={tombolNada}><ChevronUp size={22} /></TombolLunak>
          <TombolLunak bisa={nadaBisa} mati={a.geser_nada === 0} alasan={alasanNada} kabari={kabari}
            label={`${t('kontrol.nada')} · ${t('kontrol.reset')}`} onClick={() => ubah({ geser_nada: 0 })}
            className="w-9 h-9 mr-0.5 rounded-full grid place-items-center text-text-3 hover:text-text hover:bg-white/10">
            <RotateCcw size={16} />
          </TombolLunak>
        </div>

        <TombolSaklar label={t('kontrol.vokal')} nyala={vokalNyala} bisa={vokalBisa} alasan={alasanVokal} kabari={kabari}
          onClick={() => ubah({ vokal_panduan: !a.vokal_panduan })}
          ikon={vokalNyala ? <Mic2 size={22} /> : <MicOff size={22} />} />
        <TombolSaklar label={t('kontrol.musik')} nyala={musik} bisa={musikBisa} alasan={sp ? alasanSp : ''} kabari={kabari}
          judul={yt ? t('kontrol.muteYt') : undefined}
          onClick={() => ubah({ musik_nyala: !musik })}
          ikon={<span className="relative grid place-items-center"><Music2 size={22} />{!musik && coretan}</span>} />

        <Volume nilai={a.volume ?? 100} bisa={volBisa} alasan={alasanSp} kabari={kabari} ubah={ubah}
          maks={a.sumber === 'youtube' ? 100 : 150} />

        {catatan && (
          <button type="button" onClick={() => kabari(catatan)} aria-label={t('kontrol.keterangan')} title={catatan}
            className="rm-tekan shrink-0 w-7 h-7 rounded-full max-[359px]:hidden grid place-items-center text-text-3 hover:text-gold-1 hover:bg-white/[0.08]">
            <Info size={15} />
          </button>
        )}
      </div>

      {/* ── 21 Okt 2026: geser lirik YouTube/Spotify — Zaldy: "lirik ga sync
          sama sekali kalau play by YouTube". Lirik LRCLIB milik rekaman asli,
          video karaoke intronya beda → digeser dari sini, tersimpan PER VIDEO
          di server (kr_lirik_luar.geser_detik), bukan per TV. Baris muncul
          hanya kalau TV sudah menemukan liriknya (lirik_geser bukan null). ── */}
      {/* 2 Nov 2026 — Zaldy: "takeout lirik di on/off-nya". Baris lirik
          (nyala/mati + geser) dicabut dari remote tamu; lirik YouTube tetap
          mengikuti setelan per video di server (bawaan mati). */}
    </div>
  )
}

function TombolSaklar({ label, nyala, bisa, alasan, judul, kabari, onClick, ikon }: {
  label: string; nyala: boolean; bisa: boolean; alasan: string; judul?: string
  kabari: (s: string) => void; onClick: () => void; ikon: ReactNode
}) {
  const { t } = useBahasa()
  const aktif = nyala && bisa
  return (
    <TombolLunak bisa={bisa} alasan={alasan} kabari={kabari} onClick={onClick}
      label={`${label}: ${nyala ? t('kontrol.on') : t('kontrol.off')}${judul ? ` — ${judul}` : ''}`}
      className={`relative shrink-0 w-[60px] h-14 rounded-2xl border flex flex-col items-center justify-center gap-1 transition-colors duration-200 ${
        aktif ? 'bg-gold/15 border-gold/40 text-gold-1' : 'bg-white/[0.04] border-white/10 text-text-3 hover:text-text-2'}`}>
      {ikon}
      <span className="text-[11.5px] font-semibold leading-none tracking-wide max-w-[54px] truncate">{label}</span>
      <span className={`absolute top-1 right-1 w-1.5 h-1.5 rounded-full transition-colors duration-200 ${
        aktif ? 'bg-gold-1 shadow-[0_0_6px_rgba(240,214,122,.9)]' : 'bg-white/20'}`} />
    </TombolLunak>
  )
}

// Volume: ≥ lg ikon (bisu ↔ kembali) + penggeser sebaris; < lg tombol yang
// membuka popover kecil. Nilai dikirim 300 ms sesudah berhenti menggeser —
// bukan tiap langkah — supaya TV tidak dibanjiri perintah.
function Volume({ nilai, bisa, alasan, kabari, ubah, maks = 150 }: {
  nilai: number; bisa: boolean; alasan: string; kabari: (s: string) => void; ubah: (isi: IsiSetelan) => void
  /** 21 Okt 2026: 150 untuk lagu lokal (boost, limiter di TV), 100 untuk YouTube. */
  maks?: number
}) {
  // 21 Okt 2026 — Zaldy: "kurang volumenya, tambahin button plus minus, jangan
  // slide doang susah". Tombol − / + besar (langkah 10) selalu terlihat, angka
  // di tengah; ketuk angka = bisu / kembali. Penggeser tetap ada di tablet
  // lebar sebagai pelengkap. Di atas 100 = BOOST.
  const { t } = useBahasa()
  const [lokal, setLokal] = useState(nilai)
  const sibuk = useRef(false)
  const tunda = useRef<number | undefined>(undefined)
  const sebelum = useRef(nilai > 0 ? nilai : 80)

  useEffect(() => { if (!sibuk.current) setLokal(nilai) }, [nilai])
  useEffect(() => () => window.clearTimeout(tunda.current), [])

  const kirim = (v: number) => {
    const n = Math.max(0, Math.min(maks, Math.round(v)))
    setLokal(n)
    if (n > 0) sebelum.current = n
    sibuk.current = true
    window.clearTimeout(tunda.current)
    tunda.current = window.setTimeout(() => { sibuk.current = false; ubah({ volume: n }) }, 250)
  }
  const Ikon = lokal === 0 ? VolumeX : lokal < 50 ? Volume1 : Volume2
  const boost = lokal > 100
  const tombol = 'rm-tekan w-12 h-12 shrink-0 rounded-full bg-white/[0.07] border border-white/10 grid place-items-center text-[26px] font-bold leading-none disabled:opacity-30'

  return (
    <div className="shrink-0 flex items-center gap-1 rounded-full bg-white/[0.05] border border-white/10 p-0.5"
      title={bisa ? `${t('kontrol.volume')} ${lokal}` : alasan}>
      <TombolLunak bisa={bisa} mati={lokal <= 0} alasan={alasan} kabari={kabari} label={`${t('kontrol.volume')} −`}
        onClick={() => kirim(lokal - 10)} className={tombol}>−</TombolLunak>
      <TombolLunak bisa={bisa} alasan={alasan} kabari={kabari} label={`${t('kontrol.volume')} ${lokal}`}
        onClick={() => kirim(lokal > 0 ? 0 : sebelum.current)}
        className={`w-14 h-12 flex flex-col items-center justify-center gap-1 rounded-xl ${boost ? 'text-gold-1' : 'text-text-2'}`}>
        <Ikon size={20} />
        <span className="text-[14px] font-bold tabular-nums leading-none">{boost ? `${lokal} ⚡` : lokal}</span>
      </TombolLunak>
      <TombolLunak bisa={bisa} mati={lokal >= maks} alasan={alasan} kabari={kabari} label={`${t('kontrol.volume')} +`}
        onClick={() => kirim(lokal + 10)} className={tombol}>+</TombolLunak>
      <div className="hidden xl:block w-24 pr-2">
        <input type="range" min={0} max={maks} step={5} value={lokal} disabled={!bisa}
          onChange={e => kirim(Number(e.target.value))} aria-label={t('kontrol.volume')}
          className="rm-geser w-full" style={{ ['--isi' as string]: `${(lokal / maks) * 100}%` }} />
      </div>
    </div>
  )
}

// Progress bar: posisi + umur laporan dari TV (lewat poll keadaan room),
// diinterpolasi tiap 250 ms selama lagu jalan; berhenti saat dijeda. Isi
// batang & teks waktu ditulis langsung ke DOM — tidak ada render React per
// detak. Kalau remote sudah meminta jeda tapi TV belum melapor, angka
// dibekukan di posisi terakhir (tidak lari lalu mundur).
function Progres({ a, papan, perkiraan }: { a: BarisAntrean; papan: PapanPosisi; perkiraan: boolean }) {
  const { t } = useBahasa()
  const pos = useSyncExternalStore(papan.dengar, papan.baca)
  const batangRef = useRef<HTMLDivElement>(null)
  const isiRef = useRef<HTMLDivElement>(null)
  const kiriRef = useRef<HTMLSpanElement>(null)
  const beku = useRef<number | null>(null)
  const cocok = pos && pos.id === a.id ? pos : null
  const durasi = cocok && cocok.durasi > 0 ? cocok.durasi : (a.durasi_detik ?? 0)

  useEffect(() => {
    const laju = cocok?.laju || 1
    const dasar = cocok ? cocok.posisi + (cocok.jalan ? (cocok.umur_ms / 1000) * laju : 0) : 0
    const maju = !!cocok?.jalan && !a.jeda
    const gambar = () => {
      let x = dasar
      if (cocok?.jalan && a.jeda) x = beku.current ?? dasar
      else if (maju && cocok) x = dasar + ((performance.now() - cocok.diterima) / 1000) * laju
      if (durasi > 0) x = Math.min(x, durasi)
      x = Math.max(0, x)
      beku.current = x
      const persen = durasi > 0 ? x / durasi : 0
      if (isiRef.current) isiRef.current.style.transform = `translateX(${(persen - 1) * 100}%)`
      if (batangRef.current) batangRef.current.setAttribute('aria-valuenow', String(Math.floor(x)))
      const teks = jam(Math.floor(x))
      if (kiriRef.current && kiriRef.current.textContent !== teks) kiriRef.current.textContent = teks
    }
    gambar()
    if (!maju) return
    const iv = window.setInterval(gambar, 250)
    return () => window.clearInterval(iv)
  }, [cocok, a.jeda, durasi])

  return (
    <div className="[grid-area:prog] flex items-center gap-2.5 min-w-0" title={perkiraan ? t('kontrol.perkiraan') : undefined}>
      <span ref={kiriRef} className="w-10 shrink-0 text-right text-[11.5px] tabular-nums text-text-3" />
      <div ref={batangRef} role="progressbar" aria-label={t('kontrol.posisi')}
        aria-valuemin={0} aria-valuemax={Math.round(durasi)}
        className="relative flex-1 h-1.5 rounded-full bg-white/10 overflow-hidden">
        <div ref={isiRef} className="rm-progres absolute inset-0 rounded-full bg-gradient-to-r from-gold-3 via-gold-2 to-gold-1 -translate-x-full" />
      </div>
      <span className="w-10 shrink-0 text-[11.5px] tabular-nums text-text-3">
        {perkiraan && durasi > 0 ? '≈' : ''}{durasi > 0 ? jam(Math.round(durasi)) : '–:––'}
      </span>
    </div>
  )
}

// ── LAYANAN ROOM (2 Okt 2026) ─────────────────────────────────────────────
//
// Zaldy: "tambahin kontak ke marketing, otomatis kelihatan pesanan yang
// dipesan, terus juga bisa call waitress".
// Pesanan F&B dibaca dari kasir super-app lewat booking ElPOS room ini;
// sesi walk-in tanpa booking hanya menampilkan tombol panggil.

const LABEL_STATUS: Record<string, [Kunci, string]> = {
  belum: ['status.belum', 'text-text-3 border-white/15'],
  diproses: ['status.diproses', 'text-amber-300 border-amber-400/40'],
  diantar: ['status.diantar', 'text-emerald-300 border-emerald-400/40'],
}

function PanelLayanan({ kode, kabari }: { kode: string; kabari: (s: string) => void }) {
  const [d, setD] = useState<Layanan | null>(null)
  const [sibuk, setSibuk] = useState<'' | 'waitress' | 'marketing'>('')
  const { b, t } = useBahasa()

  const muat = useCallback(async () => {
    try { setD(await ambilLayanan(kode)) } catch { /* diam: panel tambahan */ }
  }, [kode])

  // 5 Okt 2026: 20 dtk → 8 dtk selama ada panggilan yang belum di-OK, supaya
  // "… sedang menuju" muncul tak lama sesudah penerima menekan OK.
  const adaMenunggu = d?.panggil?.waitress.status === 'menunggu' || d?.panggil?.marketing.status === 'menunggu'
  useEffect(() => {
    void muat()
    const iv = window.setInterval(() => { if (!document.hidden) void muat() }, adaMenunggu ? 8000 : 20000)
    return () => window.clearInterval(iv)
  }, [muat, adaMenunggu])

  async function panggil() {
    if (sibuk) return
    setSibuk('waitress')
    try {
      const j = await panggilWaitress(kode)
      // Server menjawab kalimat Indonesia + `kode`. Untuk bahasa lain dipetakan
      // ke terjemahannya; untuk Indonesia kalimat server dipakai apa adanya.
      const sudah = j.kode ? j.kode === 'sudah' : /sudah/i.test(j.pesan ?? '')
      if (b === 'id') kabari(j.pesan || t('layanan.dipanggil'))
      else kabari(sudah ? t('layanan.sudahDipanggil') : t('layanan.dipanggil'))
      void muat()
    } catch (e) { kabari(pesanGalat(e)) } finally { setSibuk('') }
  }

  // 5 Okt 2026: Panggil Marketing — hanya marketing booking room ini yang
  // menerima (bunyi di ElPOS akunnya). Batas 60 dtk sama dengan waitress.
  async function panggilMkt() {
    if (sibuk) return
    setSibuk('marketing')
    try {
      const j = await panggilMarketing(kode)
      const k = j.kode ?? (j.ok ? 'dipanggil' : 'tak_ada')
      if (b === 'id') kabari(j.pesan)
      else kabari(k === 'sudah' ? t('layanan.marketingSudah') : k === 'tak_ada' ? t('layanan.marketingTakAda') : t('layanan.marketingDipanggil'))
      void muat()
    } catch (e) { kabari(pesanGalat(e)) } finally { setSibuk('') }
  }

  // Remote lama → server baru (atau sebaliknya): `panggil` bisa tidak ada.
  const stW = d?.panggil?.waitress.status ?? (d?.panggilan_aktif ? 'menunggu' : '')
  const stM = d?.panggil?.marketing.status ?? ''
  const mktBisa = !!d?.marketing && d.marketing.bisa_dipanggil !== false

  const gayaTombol = (st: string) =>
    st === 'menuju' ? 'bg-emerald-500/15 text-emerald-200 border border-emerald-400/40'
    : st === 'menunggu' ? 'bg-amber-400/15 text-amber-200 border border-amber-400/40'
    : 'bg-gradient-to-b from-gold-1 to-gold text-black shadow-[0_10px_24px_-12px_rgba(212,175,55,.8)]'

  const baris = (d?.pesanan?.bill ?? [])
    .filter(b => b.status !== 'batal')
    .flatMap(b => b.baris)
  const barisBelum = baris.filter(b => b.status !== 'diantar')
  const barisSudah = baris.filter(b => b.status === 'diantar')

  return (
    <section className="rounded-[24px] bg-white/[0.035] border border-white/[0.08] p-4 space-y-4">
      {/* 5 Okt 2026: Panggil Waitress + Panggil Marketing berdampingan.
          Marketing hanya tampil kalau booking room ini punya marketing. */}
      {/* 6 Okt 2026 (Zaldy: "panggil marketing … blm ada ya?"): tombol
          Marketing SELALU tampil. Room tanpa marketing → tombolnya redup dan
          ketukannya menjelaskan sebabnya, bukan tombol yang hilang. */}
      <div className="grid gap-2 grid-cols-2">
        <button onClick={panggil} disabled={!!sibuk}
          className={`rm-tekan w-full min-h-12 px-2 py-2 rounded-2xl font-semibold flex items-center justify-center gap-2 text-[14.5px] leading-tight text-center ${gayaTombol(stW)}`}>
          {sibuk === 'waitress' ? <Loader2 size={17} className="animate-spin shrink-0" /> : <BellRing size={17} className="shrink-0" />}
          <span>{stW === 'menuju' ? t('layanan.menuju') : stW === 'menunggu' ? t('layanan.menungguWaitress') : t('layanan.panggil')}</span>
        </button>
        <button
          onClick={() => (mktBisa ? panggilMkt() : kabari(d?.marketing ? t('layanan.marketingTakAda') : t('layanan.tanpaMarketing')))}
          disabled={!!sibuk}
          title={mktBisa ? undefined : (d?.marketing ? t('layanan.marketingTakAda') : t('layanan.tanpaMarketing'))}
          className={`rm-tekan w-full min-h-12 px-2 py-2 rounded-2xl font-semibold flex items-center justify-center gap-2 text-[14.5px] leading-tight text-center
            ${mktBisa ? gayaTombol(stM) : 'bg-white/5 text-text-3 border border-white/10'}`}>
          {sibuk === 'marketing' ? <Loader2 size={17} className="animate-spin shrink-0" /> : <User size={17} className="shrink-0" />}
          <span>{stM === 'menuju' ? t('layanan.marketingMenuju') : stM === 'menunggu' ? t('layanan.marketingMenunggu') : t('layanan.panggilMarketing')}</span>
        </button>
      </div>
      {d?.marketing && !mktBisa && (
        <div className="text-[12px] text-text-3 -mt-2">{t('layanan.marketingTakAda')}</div>
      )}

      {d?.marketing && (
        <div className="flex items-center gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-[12px] text-text-3">{t('layanan.marketing')}</div>
            <div className="text-[15px] font-semibold truncate">{d.marketing.nama}</div>
          </div>
          {d.marketing.hp && (
            <a href={`https://wa.me/${d.marketing.hp}`} target="_blank" rel="noreferrer"
              className="rm-tekan shrink-0 inline-flex items-center gap-1.5 text-[13.5px] font-medium px-3.5 py-2 rounded-full
                         bg-emerald-500/15 text-emerald-300 border border-emerald-400/30">
              <MessageCircle size={15} /> WhatsApp
            </a>
          )}
        </div>
      )}

      {/* 6 Okt 2026 (Zaldy: "cek pesanan … jadi keliatan pesanan yg udah
          datang dan yg belum dr sisi pengguna"): bagian pesanan SELALU tampil,
          dipisah Belum datang (belum/diproses) vs Sudah datang (diantar). */}
      <div>
        <div className="text-[14px] font-semibold mb-2 flex items-center gap-2">
          <UtensilsCrossed size={15} className="text-gold" /> {t('layanan.pesanan')}
          {baris.length > 0 && (
            <span className="ml-auto text-[12px] font-normal text-text-3">
              {barisSudah.length}/{baris.length} {t('layanan.sudahDatang').toLowerCase()}
            </span>
          )}
        </div>
        {baris.length === 0 ? (
          <div className="text-[13px] text-text-3">{t('layanan.kosong')}</div>
        ) : (
          <div className="space-y-3">
            {[
              { judul: t('layanan.belumDatang'), isi: barisBelum, warna: 'text-amber-300', titik: 'bg-amber-400' },
              { judul: t('layanan.sudahDatang'), isi: barisSudah, warna: 'text-emerald-300', titik: 'bg-emerald-400' },
            ].filter(g => g.isi.length > 0).map(g => (
              <div key={g.judul}>
                <div className={`flex items-center gap-1.5 text-[12px] uppercase tracking-[0.14em] mb-1.5 ${g.warna}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${g.titik}`} /> {g.judul} · {g.isi.length}
                </div>
                <ul className="space-y-1.5">
                  {g.isi.map((b, i) => {
                    const [label, warna] = LABEL_STATUS[b.status] ?? LABEL_STATUS.belum
                    return (
                      <li key={i} className={`flex items-center gap-2 text-[13.5px] ${b.status === 'diantar' ? 'opacity-75' : ''}`}>
                        <span className="text-text-3 w-8 shrink-0 text-right">{b.qty}×</span>
                        <span className="min-w-0 flex-1 truncate">{b.nama}</span>
                        <span className={`shrink-0 text-[11.5px] px-2 py-0.5 rounded-full border ${warna}`}>{t(label)}</span>
                      </li>
                    )
                  })}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  )
}

// ── PANEL ANTREAN ──────────────────────────────────────────────────────────
//
// Hanya daftar. "Sedang diputar" + kontrol audio pindah ke bilah mengambang
// (dulu ada kartu Now Playing di sini — dobel). Tidak ada tombol "Mulai":
// lagu pertama langsung diputar oleh server (antrean_otomatis.go).
// 5 Okt sore: baris yang diputar tidak lagi punya tombol buka-kontrol —
// semua kontrolnya sudah terlihat di bilah putar.

function PanelAntrean({ kode, antrean, sesudah }: {
  kode: string; antrean: BarisAntrean[]; sesudah: () => void
}) {
  const [galat, setGalat] = useState('')
  const [buka, setBuka] = useState<string | null>(null)
  const { t } = useBahasa()
  // nadaTampil (lib/tamu) menulis "Nada asli" untuk geser 0; sisanya hanya
  // nama nada dan angka, jadi cukup kasus itu yang diterjemahkan di sini.
  const nada = (dasar: string | null, geser: number) => geser === 0 ? t('nada.asli') : nadaTampil(dasar, geser)

  async function ubah(a: BarisAntrean, isi: IsiSetelan) {
    setGalat('')
    try { await setelan(kode, a.id, isi); sesudah() } catch (e) { setGalat(pesanGalat(e)) }
  }
  async function buang(a: BarisAntrean) {
    setGalat('')
    try { await batalAntre(kode, a.id); sesudah() } catch (e) { setGalat(pesanGalat(e)) }
  }
  // 8 Okt 2026: putar lagu antrean ini SEKARANG — yang sedang diputar ditutup
  // oleh server dalam pernyataan yang sama (TamuMainkan 'mulai').
  const [sibukPutar, setSibukPutar] = useState('')
  async function putarSekarang(a: BarisAntrean) {
    if (sibukPutar) return
    setGalat(''); setSibukPutar(a.id)
    try { await tandai(kode, a.id, 'mulai'); sesudah() } catch (e) { setGalat(pesanGalat(e)) }
    finally { setSibukPutar('') }
  }

  const setelanUntuk = antrean.find(a => a.id === buka && a.status !== 'diputar')
  let nomor = 0

  // ── 27 Okt 2026 — Zaldy: "list lagu yang sudah dipilih bisa di-drag-n-drop,
  // nomor 9 mau dinaikkan ke 7". Pegangan ≡ di kiri baris; tarik ke atas/bawah,
  // lepas → urutan baru dikirim ke server (POST antrean/urutkan). Selama
  // ditarik, daftar lokal yang dipakai supaya polling 4 dtk tidak melompat.
  const [seret, setSeret] = useState<{ id: string; urut: string[]; y: number } | null>(null)
  const barisRef = useRef<Map<string, HTMLDivElement>>(new Map())
  const urutTampil = seret ? seret.urut : antrean.map(a => a.id)
  const daftar = urutTampil.map(id => antrean.find(a => a.id === id)).filter((a): a is BarisAntrean => !!a)
  const mulaiSeret = (a: BarisAntrean, e: PointerEventReact<HTMLElement>) => {
    if (a.status === 'diputar') return
    e.preventDefault()
    ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
    setSeret({ id: a.id, urut: antrean.map(x => x.id), y: e.clientY })
  }
  const gerakSeret = (e: PointerEventReact<HTMLElement>) => {
    if (!seret) return
    const y = e.clientY
    // Baris menunggu yang titik tengahnya dilewati penunjuk → tukar tempat.
    const dari = seret.urut.indexOf(seret.id)
    let ke = dari
    for (let i = 0; i < seret.urut.length; i++) {
      const id = seret.urut[i]
      if (id === seret.id) continue
      if (antrean.find(x => x.id === id)?.status === 'diputar') continue
      const el = barisRef.current.get(id)
      if (!el) continue
      const r = el.getBoundingClientRect()
      const tengah = r.top + r.height / 2
      if (i < dari && y < tengah) { ke = Math.min(ke, i) }
      if (i > dari && y > tengah) { ke = Math.max(ke, i) }
    }
    if (ke !== dari) {
      const u = seret.urut.slice()
      u.splice(dari, 1); u.splice(ke, 0, seret.id)
      setSeret({ ...seret, urut: u, y })
    }
  }
  const lepasSeret = async () => {
    if (!seret) return
    const baru = seret.urut
    const lama = antrean.map(x => x.id)
    setSeret(null)
    if (baru.join() === lama.join()) return
    setGalat('')
    try {
      await urutkanAntrean(kode, baru.filter(id => antrean.find(x => x.id === id)?.status === 'menunggu'))
      sesudah()
    } catch (e) { setGalat(pesanGalat(e)) }
  }

  return (
    <>
      <Kartu lentur judul={t('antre.judul')} ikon={<Music2 size={15} className="text-gold" />}
        kanan={<span className="text-[12px] text-text-3">{t('antre.jumlah', { n: antrean.length })}</span>}>
        {galat && <div className="mb-2 text-[13px] text-red-300">{galat}</div>}
        {antrean.length === 0 ? (
          <div className="py-9 text-center rm-pudar">
            <span className="mx-auto w-12 h-12 rounded-2xl bg-white/[0.05] grid place-items-center text-text-3"><Music2 size={20} /></span>
            <div className="mt-3 text-[13.5px] text-text-3 leading-relaxed max-w-[260px] mx-auto">{t('antre.kosong')}</div>
          </div>
        ) : (
          <div className="space-y-1.5 max-h-[52vh] tab:max-h-none tab:flex-auto tab:min-h-0 overflow-y-auto overscroll-contain pr-1 -mr-1">
            {daftar.map((a, k) => {
              const putar = a.status === 'diputar'
              if (!putar) nomor++
              const ditarik = seret?.id === a.id
              return (
                <div key={a.id} style={{ ['--i' as string]: Math.min(k, 10) }}
                  ref={el => { if (el) barisRef.current.set(a.id, el); else barisRef.current.delete(a.id) }}
                  className={`${seret ? '' : 'rm-muncul '}rounded-2xl px-2.5 py-2.5 flex items-center gap-2.5 border transition-colors ${
                    putar ? 'bg-gradient-to-r from-[#5b6cff]/90 to-[#8a5cff]/80 border-white/20 shadow-[0_12px_30px_-12px_rgba(91,108,255,.85)]'
                          : ditarik ? 'bg-gold/10 border-gold/40 shadow-[0_10px_24px_-10px_rgba(212,175,55,.6)]'
                          : 'bg-white/[0.03] border-white/[0.06] hover:bg-white/[0.06]'}`}>
                  {/* 30 Okt (Zaldy, screenshot: "jadi gatau ini lagu apaan"): kolom
                      teks kejepit pegangan + nomor + 2 tombol, judul hilang tinggal
                      label "YouTube…". Sekarang: nomor menempel di sudut sampul,
                      label sumber turun ke baris artis, judul dapat satu baris
                      penuh dan boleh 2 baris (tidak dipotong). */}
                  {putar ? (
                    <span className="w-6 grid place-items-center text-white shrink-0"><Ekualiser diam={!!a.jeda} /></span>
                  ) : (
                    <span onPointerDown={e => mulaiSeret(a, e)} onPointerMove={gerakSeret}
                      onPointerUp={() => void lepasSeret()} onPointerCancel={() => void lepasSeret()}
                      aria-label={t('antre.geser')} title={t('antre.geser')}
                      className="w-6 h-9 -ml-1 grid place-items-center text-text-3 cursor-grab active:cursor-grabbing shrink-0"
                      style={{ touchAction: 'none' }}>
                      <GripVertical size={16} />
                    </span>
                  )}
                  <span className="relative shrink-0">
                    <Sampul judul={a.judul} artis={a.artis} gambar={a.sampul || undefined} warna={WARNA_KARTU[k % WARNA_KARTU.length]}
                      className="w-11 h-11 rounded-xl text-[13px]" />
                    {!putar && (
                      <span className="absolute -top-1.5 -left-1.5 min-w-[20px] h-5 px-1 rounded-full bg-[#0c0f24] border border-white/20
                                       text-[10.5px] font-semibold tabular-nums grid place-items-center text-text-2">{nomor}</span>
                    )}
                  </span>
                  <button onClick={() => { if (!putar) void putarSekarang(a) }} className={`min-w-0 flex-1 text-left ${putar ? 'cursor-default' : ''}`}>
                    <span className="block text-[16px] font-medium leading-snug line-clamp-2 break-words">{a.judul}</span>
                    <span className={`mt-0.5 flex items-center gap-1.5 min-w-0 text-[14px] ${putar ? 'text-white/75' : 'text-text-3'}`}>
                      {a.sumber === 'youtube' && <span className="shrink-0 text-[10px] px-1.5 py-px rounded bg-red-500/20 text-red-200">YouTube</span>}
                      {a.sumber === 'spotify' && <span className="shrink-0 text-[10px] px-1.5 py-px rounded bg-emerald-500/20 text-emerald-300">Spotify</span>}
                      <span className="truncate">
                        {a.artis}{a.diminta_oleh ? ` · ${a.diminta_oleh}` : ''}
                        {(a.geser_nada !== 0 || a.tempo_persen !== 100) && ` · ${nada(a.nada_dasar, a.geser_nada)}${a.tempo_persen !== 100 ? ` ${a.tempo_persen}%` : ''}`}
                      </span>
                    </span>
                  </button>
                  {putar ? null : (
                    <>
                      {/* 8 Okt 2026 — Zaldy: "pengaturan nada di antrean nggak usah, sudah
                          ada di bawah; kalau diklik ada tombol play-nya aja". */}
                      <button onClick={() => void putarSekarang(a)} title={t('antre.putar')} aria-label={t('antre.putar')}
                        disabled={sibukPutar === a.id}
                        className="rm-tekan w-9 h-9 rounded-full bg-gradient-to-b from-gold-1 to-gold grid place-items-center text-black shrink-0 disabled:opacity-60">
                        {sibukPutar === a.id ? <Loader2 size={15} className="animate-spin" /> : <Play size={15} fill="currentColor" className="ml-0.5" />}
                      </button>
                      <button onClick={() => buang(a)} title={t('antre.batalkan')} aria-label={t('antre.batalkan')}
                        className="rm-tekan w-8 h-8 rounded-full bg-white/5 border border-white/10 grid place-items-center text-text-3 shrink-0"><Trash2 size={14} /></button>
                    </>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </Kartu>

      {setelanUntuk && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-3" onClick={() => setBuka(null)}>
          <div className="absolute inset-0 bg-black/60 rm-pudar" />
          <div className="relative w-full max-w-md rounded-[26px] bg-gradient-to-b from-[#181d40] to-[#0d1028] border border-white/[0.12] p-5 rm-lembar"
               onClick={e => e.stopPropagation()}>
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <div className="text-[16px] font-semibold truncate">{setelanUntuk.judul}</div>
                <div className="text-[12.5px] text-text-3 truncate">{setelanUntuk.artis}</div>
              </div>
              <button onClick={() => setBuka(null)} className="rm-tekan w-9 h-9 rounded-full bg-white/5 grid place-items-center"><X size={16} /></button>
            </div>
            {setelanUntuk.sumber === 'spotify' ? (
              <div className="mt-4 text-[13px] text-text-3 leading-relaxed">
                {t('kontrol.spotifyLokal')}
              </div>
            ) : <>
            {/* 5 Okt 2026: baris YouTube boleh disetel Nada & Vokal (dipakai bila
                TV-nya PC dengan EL Audio aktif). Tempo tetap mati untuk YouTube. */}
            <div className="mt-4 flex items-center gap-2">
              <span className="text-[12px] text-text-3 w-12 shrink-0">{t('setelan.nada')}</span>
              <Stepper nilai={setelanUntuk.geser_nada} min={-6} maks={6}
                ubah={v => ubah(setelanUntuk, { geser_nada: v })} label={nada(setelanUntuk.nada_dasar, setelanUntuk.geser_nada)} />
            </div>
            {setelanUntuk.sumber === 'youtube' ? (
              <div className="mt-2 flex items-center gap-2">
                <span className="text-[12px] text-text-3 w-12 shrink-0">{t('setelan.tempo')}</span>
                <span className="flex-1 text-center text-[12.5px] text-text-3 py-2 rounded-xl border border-white/5 bg-white/[0.02]">
                  {t('setelan.tempoTidakYt')}
                </span>
              </div>
            ) : (
            <div className="mt-2 flex items-center gap-2">
              <span className="text-[12px] text-text-3 w-12 shrink-0">{t('setelan.tempo')}</span>
              <Stepper nilai={setelanUntuk.tempo_persen} min={70} maks={130} langkah={5}
                ubah={v => ubah(setelanUntuk, { tempo_persen: v })}
                label={setelanUntuk.tempo_persen === 100 ? t('setelan.normal') : `${setelanUntuk.tempo_persen}%`} ikon={<Gauge size={13} />} />
            </div>
            )}
            <div className="mt-2 flex items-center gap-2">
              <span className="text-[12px] text-text-3 w-12 shrink-0">{t('setelan.vokal')}</span>
              <button onClick={() => ubah(setelanUntuk, { vokal_panduan: !setelanUntuk.vokal_panduan })}
                className={`rm-tekan flex-1 rounded-xl py-2 text-[13px] border ${setelanUntuk.vokal_panduan ? 'border-gold/40 bg-gold/10 text-gold' : 'border-white/10 bg-white/5 text-text-3'}`}>
                {setelanUntuk.vokal_panduan ? t('setelan.vokalNyala') : t('setelan.vokalMati')}
              </button>
            </div>
            {setelanUntuk.sumber === 'youtube' && (
              <div className="mt-2 text-[11.5px] text-text-3 leading-snug">
                {t('setelan.ytCatatan')}
              </div>
            )}
            {setelanUntuk.sumber !== 'youtube' && !setelanUntuk.vokal_panduan && !setelanUntuk.ada_instrumental && (
              <div className="mt-2 text-[11.5px] text-text-3 leading-snug">
                {t('setelan.tanpaInstrumental')}
              </div>
            )}
            {setelanUntuk.sumber !== 'youtube' && !setelanUntuk.ada_melodi && (
              <div className="mt-1.5 text-[11.5px] text-text-3 leading-snug">
                {t('setelan.acuan1')}<span className="text-text-2">{t('setelan.acuanIrama')}</span>{t('setelan.acuan2')}
              </div>
            )}
            </>}
            <button onClick={() => { void buang(setelanUntuk); setBuka(null) }}
              className="rm-tekan mt-4 w-full py-2.5 rounded-xl border border-red-500/30 text-red-300 text-[13.5px] inline-flex items-center justify-center gap-1.5">
              <Trash2 size={14} /> {t('setelan.batalAntre')}
            </button>
          </div>
        </div>
      )}
    </>
  )
}

// ── KONTROL AUDIO LAGU YANG SEDANG DIPUTAR (3 Okt 2026) ─────────────────────
//
// Vokal ON/OFF · Musik ON/OFF · Nada −/+ (−6…+6) + reset · Jeda · Volume ·
// Lewati. Perintah dikirim lewat setelan antrean (di-poll pemutar TV
// ±2,5 dtk); keadaan yang tampil adalah yang tersimpan di antrean, dan
// kemampuan tombol dibaca dari laporan pemutar (`pemutar`) — kalau belum ada
// laporan, ditebak dari berkas yang tersedia. Dipakai KontrolPutar (bilah).
type IsiSetelan = Parameters<typeof setelan>[2]

function kemampuan(a: BarisAntrean) {
  const sumber = a.sumber ?? 'lokal'
  const lap = a.pemutar ?? null
  const lokal = sumber === 'lokal', sp = sumber === 'spotify', yt = sumber === 'youtube'
  const adaStem = !!a.ada_instrumental
  // 5 Okt 2026: YouTube mengikuti laporan pemutar. Vokal & nada hidup hanya
  // kalau TV-nya PC dengan ekstensi EL Audio aktif (pemutar melapor
  // vokal_bisa/nada_bisa true); tanpa laporan = mati, seperti dulu.
  const vokalBisa = lokal ? (lap ? lap.vokal_bisa : adaStem) : yt ? !!lap?.vokal_bisa : false
  const musikBisa = !sp && (lap ? lap.musik_bisa : true)
  const nadaBisa = lokal ? (lap ? lap.nada_bisa : true) : yt ? !!lap?.nada_bisa : false
  return { lokal, sp, yt, lap, vokalBisa, musikBisa, nadaBisa, jedaBisa: !sp, volBisa: !sp }
}

function Stepper({ nilai, min, maks, langkah = 1, ubah, label, ikon }: {
  nilai: number; min: number; maks: number; langkah?: number
  ubah: (v: number) => void; label: string; ikon?: ReactNode
}) {
  const batas = (v: number) => Math.min(maks, Math.max(min, v))
  return (
    <div className="flex-1 flex items-center gap-1.5">
      <button onClick={() => ubah(batas(nilai - langkah))} disabled={nilai <= min}
        className="rm-tekan w-11 h-9 rounded-xl border border-white/10 bg-white/[0.05] grid place-items-center disabled:opacity-30">
        <ChevronDown size={17} />
      </button>
      <div className="flex-1 text-center text-[13px] flex items-center justify-center gap-1.5">
        {ikon}{label}
      </div>
      <button onClick={() => ubah(batas(nilai + langkah))} disabled={nilai >= maks}
        className="rm-tekan w-11 h-9 rounded-xl border border-white/10 bg-white/[0.05] grid place-items-center disabled:opacity-30">
        <ChevronUp size={17} />
      </button>
    </div>
  )
}


// ── PAPAN SKOR ─────────────────────────────────────────────────────────────

function TabPapan({ papan }: { papan: KeadaanRoom['papan'] }) {
  const { t } = useBahasa()
  if (papan.length === 0) {
    return <div className="text-[13.5px] text-text-3 py-10 text-center leading-relaxed">
      {t('papan.kosong1')}<br />
      {t('papan.kosong2')}
    </div>
  }
  const medali = ['text-amber-300', 'text-zinc-300', 'text-amber-700']
  return (
    <div className="space-y-1.5">
      {papan.map((p, i) => (
        <div key={p.nama} style={{ ['--i' as string]: Math.min(i, 10) }}
          className="rm-muncul bg-white/[0.03] border border-white/[0.06] rounded-2xl px-3.5 py-3 flex items-center gap-3">
          <div className={`w-6 text-center font-bold text-[16px] ${medali[i] ?? 'text-text-3'}`}>
            {i + 1}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[14.5px] font-medium truncate">{p.nama}</div>
            <div className="text-[12px] text-text-3">
              {t('papan.baris', { lagu: p.lagu, rerata: p.rerata })}
              {/* Papan yang mencampur dua cara penilaian membandingkan dua
                  ukuran berbeda seolah satu. Ditandai, bukan disembunyikan. */}
              {p.mode_campur && <span className="text-amber-400/80"> · {t('papan.campur')}</span>}
            </div>
          </div>
          <div className="text-[21px] font-bold text-gold tabular-nums">{p.terbaik}</div>
        </div>
      ))}
    </div>
  )
}
